import { FileDownloadOutlined, FormatListBulletedOutlined } from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import {
	ExportDropdown,
	FormActions,
	FormCard,
	FormField,
	FormSectionTitle,
	FormSelect,
} from '@practium/ui';
import { APPLICATION_METHOD_OPTIONS } from '../../constants/maintenance.constants';
import {
	PLANNING_DEMAND_VIEW_OPTIONS,
	usePlanningForm,
	type PlanningFormState,
} from '../../hooks/usePlanningForm';
import type { FertilizationPlanAdvice } from '../../types';
import { MonthPicker } from '../MonthPicker/MonthPicker';
import { SegmentedToggle } from '../SegmentedToggle/SegmentedToggle';
import { SelectionCheckbox } from '../SelectionCheckbox/SelectionCheckbox';
import { SimpleDataTable } from '../Tables/SimpleDataTable/SimpleDataTable';
import { PanelActions } from '../PanelActions/PanelActions';
import tabStyles from '../Tabs/TabPanel.module.css';
import { DemandAdjustmentBar } from './DemandAdjustmentBar';
import { FertilizerPackModal } from './FertilizerPackModal';
import { FoliarAnalysisCard } from './FoliarAnalysisCard';
import { MonthlyBudget } from './MonthlyBudget';
import { PlanningCostsCard } from './PlanningCostsCard';
import { PlanningPackField } from './PlanningPackField';
import { ProductCostsModal } from './ProductCostsModal';
import styles from './PlanningPanel.module.css';

interface PlanningPanelProps {
	advice: FertilizationPlanAdvice;
	onSaved?: (method: PlanningFormState['method']) => void;
}

export const PlanningPanel = ({ advice, onSaved }: PlanningPanelProps) => {
	const {
		form,
		errors,
		set,
		planMonths,
		isView,
		hasCalculated,
		isCalculating,
		showIrrigationFields,
		demandView,
		setDemandView,
		fertilizerDemand,
		packOptions,
		selectedPack,
		packModal,
		openPackModal,
		closePackModal,
		handlePackSaved,
		handleDuplicatePack,
		handleClearPack,
		packProducts,
		prices,
		pricesUpdatedAt,
		informedCostsCount,
		isCostsComplete,
		costsModal,
		openCostsModal,
		closeCostsModal,
		editCosts,
		handleCostsSaved,
		hasBudget,
		budgetMonths,
		activeBudgetPeriod,
		setSelectedBudgetPeriod,
		budgetTable,
		handleExportExcel,
		handleExportPdf,
		handleCalculate,
		handleClear,
		handleSave,
		adjustment,
		handleEdit,
		methodLabel,
		startMonthLabel,
		endMonthLabel,
		packLabel,
	} = usePlanningForm({ advice, onSaved });

	const [revealIrrigation, setRevealIrrigation] = useState(false);
	const irrigationWasVisible = useRef(false);

	useEffect(() => {
		if (!showIrrigationFields) {
			irrigationWasVisible.current = false;
			setRevealIrrigation(false);
			return;
		}
		if (!irrigationWasVisible.current && !isView) {
			setRevealIrrigation(true);
		}
		irrigationWasVisible.current = true;
	}, [isView, showIrrigationFields]);

	const irrigationRevealClass = revealIrrigation ? styles.reveal : '';

	return (
		<div className={tabStyles.panel}>
			<FormCard icon={<FormatListBulletedOutlined />} title="Planejamento">
				<div className={tabStyles.form}>
					<div className={tabStyles.section}>
						<FormSectionTitle>Planejamento geral</FormSectionTitle>
						<div className={tabStyles.sectionFields}>
							<div className={tabStyles.row3}>
								<FormField label="Método de aplicação" required error={errors.method}>
									{isView ? (
										<span className={tabStyles.viewText}>{methodLabel}</span>
									) : (
										<FormSelect
											value={form.method}
											onChange={(value) => set('method', value)}
											options={APPLICATION_METHOD_OPTIONS}
											placeholder="Selecione o método"
											hasError={!!errors.method}
										/>
									)}
								</FormField>
								<FormField label="Mês inicial" required error={errors.startMonth}>
									{isView ? (
										<span className={tabStyles.viewText}>{startMonthLabel}</span>
									) : (
										<MonthPicker
											value={form.startMonth}
											onChange={(value) => set('startMonth', value)}
											placeholder="Ex.: Janeiro"
											allowedMonths={planMonths}
											hasError={!!errors.startMonth}
										/>
									)}
								</FormField>
								<FormField label="Mês final" required error={errors.endMonth}>
									{isView ? (
										<span className={tabStyles.viewText}>{endMonthLabel}</span>
									) : (
										<MonthPicker
											value={form.endMonth}
											onChange={(value) => set('endMonth', value)}
											placeholder="Ex.: Dezembro"
											allowedMonths={planMonths}
											hasError={!!errors.endMonth}
										/>
									)}
								</FormField>
							</div>

							{showIrrigationFields && (
								<div
									className={`${styles.irrigationFields} ${irrigationRevealClass}`}
									onAnimationEnd={() => setRevealIrrigation(false)}
								>
									<PlanningPackField
										isView={isView}
										packId={form.packId}
										packLabel={packLabel}
										packOptions={packOptions}
										selectedPack={selectedPack}
										error={errors.packId}
										onChange={(value) => set('packId', value)}
										onOpenModal={openPackModal}
										onDuplicate={handleDuplicatePack}
										onClear={handleClearPack}
									/>
									{!isView && form.packId && packProducts.length > 0 && (
										<div className={styles.packRow}>
											<div className={styles.packField}>
												<PlanningCostsCard
													informedCount={informedCostsCount}
													totalCount={packProducts.length}
													isComplete={isCostsComplete}
													onClick={openCostsModal}
												/>
											</div>
										</div>
									)}
								</div>
							)}
						</div>
					</div>

					{hasCalculated && (
						<div className={tabStyles.section}>
							<div className={styles.sectionHeader}>
								<FormSectionTitle>Demanda de fertilizantes</FormSectionTitle>
								<SegmentedToggle
									options={PLANNING_DEMAND_VIEW_OPTIONS}
									value={demandView}
									onChange={setDemandView}
									ariaLabel="Visualização da demanda"
								/>
								{!isView && (
									<div className={styles.adjustToggle}>
										<SelectionCheckbox
											checked={adjustment.isAdjusting}
											onChange={adjustment.toggleAdjusting}
											label="Ajustar demanda com análise foliar"
										/>
									</div>
								)}
							</div>
							<SimpleDataTable
								columns={fertilizerDemand.columns}
								rows={fertilizerDemand.rows}
								selection={
									adjustment.isAdjusting
										? {
												selectedRowIds: adjustment.selectedPeriods,
												onToggleRow: adjustment.togglePeriod,
												onToggleAll: adjustment.toggleAllPeriods,
											}
										: undefined
								}
							/>
						</div>
					)}

					{hasCalculated && adjustment.isAdjusting && (
						<div className={`${tabStyles.section} ${styles.reveal}`}>
							<FoliarAnalysisCard analysis={adjustment.foliarAnalysis} />
							<DemandAdjustmentBar
								selectedCount={adjustment.selectedPeriods.length}
								isApplying={adjustment.isApplying}
								onAdjust={adjustment.applyAdjustment}
							/>
						</div>
					)}

					{hasCalculated && adjustment.adjustedDemand && !adjustment.isAdjusting && (
						<div className={`${tabStyles.section} ${styles.reveal}`}>
							<div className={styles.sectionHeader}>
								<FormSectionTitle>Demanda de fertilizantes ajustada</FormSectionTitle>
								<SegmentedToggle
									options={PLANNING_DEMAND_VIEW_OPTIONS}
									value={adjustment.adjustedView}
									onChange={adjustment.setAdjustedView}
									ariaLabel="Visualização da demanda ajustada"
								/>
							</div>
							<SimpleDataTable
								columns={adjustment.adjustedDemand.columns}
								rows={adjustment.adjustedDemand.rows}
							/>
						</div>
					)}

					{hasCalculated && hasBudget && (
						<MonthlyBudget
							months={budgetMonths}
							activePeriod={activeBudgetPeriod}
							onPeriodChange={setSelectedBudgetPeriod}
							table={budgetTable}
						/>
					)}
				</div>
			</FormCard>

			<PanelActions>
				{isView ? (
					<div className={styles.viewActions}>
						<FormActions mode="view" onEdit={handleEdit} />
						<ExportDropdown
							onExportExcel={handleExportExcel}
							onExportPdf={handleExportPdf}
							icon={<FileDownloadOutlined />}
							showChevron={false}
							dropdownPlacement="top"
							buttonClassName={styles.exportButton}
						/>
					</div>
				) : (
					<FormActions
						onClear={handleClear}
						onSecondary={handleCalculate}
						secondaryText="Calcular"
						secondaryDisabled={isCalculating}
						secondaryLoading={isCalculating}
						onSave={handleSave}
						saveDisabled={!hasCalculated || isCalculating}
					/>
				)}
			</PanelActions>

			<FertilizerPackModal
				isOpen={packModal.open}
				mode={packModal.mode}
				pack={packModal.pack}
				onClose={closePackModal}
				onSaved={handlePackSaved}
				onDeleted={handleClearPack}
			/>

			<ProductCostsModal
				isOpen={costsModal.open}
				mode={costsModal.mode}
				packName={packLabel}
				products={packProducts}
				prices={prices}
				updatedAt={pricesUpdatedAt}
				canEdit={!isView}
				onClose={closeCostsModal}
				onEdit={editCosts}
				onSave={handleCostsSaved}
			/>
		</div>
	);
};
